/**
 * Home/intro department items — the FIRST quarter of the home timeline
 * (zone 'home', progress band 0–0.25). Visual layout: the debug logo and intro
 * copy sit centre-left, wrapped in three large faded gradient ambience
 * particles that drift on parallax and repell from the cursor.
 *
 * Classification rule for this file: `zone: 'home'` tag where present;
 * untagged items (introP2, introCopy) classified by their 'intro' id prefix
 * (introP2 also matches the home relativeX band 0–0.25).
 *
 * Grouping is for HUMANS ONLY — creation order is owned by index.ts (see the
 * order contract there). Field semantics: see HomeItemConfig / the underlying
 * TimelineItemConfig.
 */
import type { HomeItemConfig } from './homeItems.types';

/**
 * Gradient ambience particle at the far-left edge of the intro zone, behind the
 * intro copy. Repells away from the cursor.
 */
export const introP1: HomeItemConfig = {
	disable: false,
	id: 'introp1',
	texture: 'gradient-particle.png',
	relativeX: 0,
	relativeY: 0.2,
	easePower: 3,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.12,
	parallaxPowerMouse: 0.025,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.7,
	autoRange: 0.95,
	repell: true,
	alpha: 1.5e-5,
	scale: 2,
	zone: 'home'
};

/**
 * Gradient ambience particle just right of the intro copy. Repells away from the
 * cursor. Has NO `zone` tag — classified to intro by the 'introp' id prefix and by
 * relativeX 0.0949 falling in the home band (0–0.25).
 */
export const introP2: HomeItemConfig = {
	disable: false,
	id: 'introp2',
	texture: 'gradient-particle.png',
	relativeX: 0.0949,
	relativeY: 0.4,
	easePower: 6,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.17,
	parallaxPowerMouse: 0.017,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.25,
	autoRange: 0.5,
	repell: true,
	alpha: 3.5e-6
};

/**
 * Gradient ambience particle at the right edge of the intro zone (relativeX 1 of
 * the home zone), bridging toward the screens zone. Repells away from the cursor.
 */
export const introP3: HomeItemConfig = {
	disable: false,
	id: 'introp3',
	texture: 'gradient-particle.png',
	relativeX: 1,
	relativeY: 0.8,
	easePower: 3,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.1,
	parallaxPowerMouse: 0.125,
	autoScale: false,
	startScale: 0.0001,
	startRangeAt: 0.7,
	autoRange: 0.95,
	repell: true,
	alpha: 1.5e-5,
	scale: 2,
	zone: 'home'
};

/**
 * The intro copy sprite (intro.png), effectively transparent (alpha 1e-05).
 * Has NO `zone` tag — classified to intro by id. NOTE: unzoned relativeX 0.5 is
 * mid-timeline, which conflicts with the id — intent unclear, flagged for
 * annotation.
 */
export const introCopy: HomeItemConfig = {
	id: 'intro',
	texture: 'intro.png',
	relativeX: 0.5,
	relativeY: 0.78,
	easePower: 0,
	parallaxOffsetPower: 0,
	parallaxPowerMouse: 0,
	scale: 1,
	alpha: 1e-5
};
